/**
 * useHomepageLayoutState - Custom hook for Homepage Layout module state management
 * Extracted from Admin.jsx to reduce state complexity
 * 
 * No changes to behavior, API endpoints, or request payloads.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/apiUrl';

const getAuthHeaders = () => {
  const token = sessionStorage.getItem('golfnuts_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const useHomepageLayoutState = ({ setError, setSuccess }) => {
  // Core layout state
  const [homepageLayout, setHomepageLayout] = useState(null);
  const [loadingLayout, setLoadingLayout] = useState(false);
  const [editingLayout, setEditingLayout] = useState(null); // Working copy
  const [layoutHasChanges, setLayoutHasChanges] = useState(false);
  const [draggedSectionIndex, setDraggedSectionIndex] = useState(null);

  // Fetch homepage layout
  const fetchHomepageLayout = useCallback(async () => {
    setLoadingLayout(true);
    try {
      const response = await axios.get(`${API_URL}/api/cms/homepage-layout`, {
        headers: getAuthHeaders()
      });
      setHomepageLayout(response.data);
      setEditingLayout(JSON.parse(JSON.stringify(response.data.sections || [])));
      setLayoutHasChanges(false);
    } catch (err) {
      console.error('Error fetching homepage layout:', err);
      setError('Failed to load homepage layout');
    } finally {
      setLoadingLayout(false);
    }
  }, [setError]);

  // Move section up/down
  const handleLayoutSectionMove = useCallback((fromIndex, toIndex) => {
    setEditingLayout(prev => {
      if (!prev || toIndex < 0 || toIndex >= prev.length) return prev;
      
      const newLayout = [...prev];
      const [movedItem] = newLayout.splice(fromIndex, 1);
      newLayout.splice(toIndex, 0, movedItem);
      return newLayout;
    });
    setLayoutHasChanges(true);
  }, []);

  // Toggle section enabled/disabled
  const handleLayoutSectionToggle = useCallback((index) => {
    setEditingLayout(prev => {
      if (!prev) return prev;
      const newLayout = [...prev];
      newLayout[index] = {
        ...newLayout[index],
        enabled: !newLayout[index].enabled
      };
      return newLayout;
    });
    setLayoutHasChanges(true);
  }, []);

  // Set section background color
  const handleLayoutSectionColor = useCallback((index, color) => {
    setEditingLayout(prev => {
      if (!prev) return prev;
      const newLayout = [...prev];
      newLayout[index] = {
        ...newLayout[index],
        backgroundColor: color || null
      };
      return newLayout;
    });
    setLayoutHasChanges(true);
  }, []);

  // Save layout changes
  const handleLayoutSave = useCallback(async () => {
    try {
      await axios.put(`${API_URL}/api/cms/homepage-layout`, {
        sections: editingLayout
      }, {
        headers: getAuthHeaders()
      });
      setSuccess('Homepage layout saved successfully!');
      setLayoutHasChanges(false);
      fetchHomepageLayout();
    } catch (err) {
      setError('Failed to save homepage layout: ' + (err.response?.data?.detail || err.message));
    }
  }, [editingLayout, fetchHomepageLayout, setError, setSuccess]);

  // Reset layout to default
  const handleLayoutReset = useCallback(async () => {
    if (!window.confirm('Are you sure you want to reset to the default layout? This will undo all custom ordering and colors.')) {
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/cms/homepage-layout/reset`, {}, {
        headers: getAuthHeaders()
      });
      setSuccess('Homepage layout reset to default');
      fetchHomepageLayout();
    } catch (err) {
      setError('Failed to reset layout: ' + (err.response?.data?.detail || err.message));
    }
  }, [fetchHomepageLayout, setError, setSuccess]);

  // Cancel/discard changes
  const handleLayoutCancel = useCallback(() => {
    if (layoutHasChanges && !window.confirm('You have unsaved changes. Are you sure you want to discard them?')) {
      return;
    }
    setEditingLayout(JSON.parse(JSON.stringify(homepageLayout?.sections || [])));
    setLayoutHasChanges(false);
  }, [layoutHasChanges, homepageLayout]);

  // Drag and drop handlers
  const handleDragStart = useCallback((e, index) => {
    setDraggedSectionIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    const dragIndex = draggedSectionIndex;
    
    if (dragIndex !== null && dragIndex !== dropIndex) {
      handleLayoutSectionMove(dragIndex, dropIndex);
    }
    setDraggedSectionIndex(null);
  }, [draggedSectionIndex, handleLayoutSectionMove]);

  const handleDragEnd = useCallback(() => {
    setDraggedSectionIndex(null);
  }, []);

  return {
    // State
    homepageLayout,
    loadingLayout,
    editingLayout,
    layoutHasChanges,
    draggedSectionIndex,

    // Setters
    setHomepageLayout,
    setEditingLayout,
    setLayoutHasChanges,
    setDraggedSectionIndex,

    // Actions
    fetchHomepageLayout,
    handleLayoutSectionMove,
    handleLayoutSectionToggle,
    handleLayoutSectionColor,
    handleLayoutSave,
    handleLayoutReset,
    handleLayoutCancel,

    // Drag and drop
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  };
};

export default useHomepageLayoutState;
